import React from 'react'
import styled from 'styled-components'
import { skip, take } from 'lib/array/arrayUtils'
import useToggle from 'hooks/useToggle'
import { pluralizeToString } from 'lib/string/pluralize'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import SlideDown from 'components/Common/SlideDown'
import ExperienceTicketItem from './ExperienceTicketItem'
import Heading from 'components/Luxkit/Typography/Heading'

const TicketOptions = styled.div`
  > *:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.palette.neutral.six};
  }
`

const AdditionalTicketOptions = styled(TicketOptions)`
  border-top: 1px solid ${props => props.theme.palette.neutral.six};
`

const CenteredToggle = styled(SmallToggleButton)`
  display: flex;
  margin: 0 auto;
`

interface Props {
  onTicketCountChange?: (ticket: App.ExperienceBookingView['ticketOptions'][number], nextCount: number) => void;
  ticketCounts: any;
  ticketOptions: Array<App.ExperienceBookingTicketView>;
  purchaseLimitRemaining?: number;
  purchaseLimitReason?: App.ExperiencePurchaseLimit['maxReason']
  className?: string;
}

const initialMaxVisibleOptions = 4

function ExperienceTicketSelection(props: Props) {
  const {
    ticketOptions,
    ticketCounts,
    onTicketCountChange,
    className,
    purchaseLimitRemaining,
    purchaseLimitReason,
  } = props
  const [showAll, toggleShowAll] = useToggle()

  return <div className={className}>
    {ticketOptions.length > 0 && <>
      <Heading variant="heading6">Select an option</Heading>
      <TicketOptions>
        {take(ticketOptions, initialMaxVisibleOptions).map(ticket =>
          <ExperienceTicketItem
            key={ticket.id}
            ticket={ticket}
            count={ticketCounts[ticket.id]}
            onTicketCountChange={onTicketCountChange}
            purchaseLimitRemaining={purchaseLimitRemaining}
            purchaseLimitReason={purchaseLimitReason}
          />)}
      </TicketOptions>
      {ticketOptions.length > initialMaxVisibleOptions && <>
        <SlideDown show={showAll}>
          <AdditionalTicketOptions>
            {skip(ticketOptions, initialMaxVisibleOptions).map(ticket =>
              <ExperienceTicketItem
                key={ticket.id}
                ticket={ticket}
                count={ticketCounts[ticket.id]}
                onTicketCountChange={onTicketCountChange}
                purchaseLimitRemaining={purchaseLimitRemaining}
                purchaseLimitReason={purchaseLimitReason}
              />)}
          </AdditionalTicketOptions>
        </SlideDown>
        <CenteredToggle
          onClick={toggleShowAll}
          size="medium"
          on={showAll}
        >
          {!showAll && <>View {pluralizeToString('more option', ticketOptions.length - initialMaxVisibleOptions)}</>}
          {showAll && <>View less options</>}
        </CenteredToggle>
      </>}
    </>}
  </div>
}

export default ExperienceTicketSelection
